import { AfterViewInit, Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { combineLatest, distinctUntilChanged, filter, Subject, takeUntil } from 'rxjs';
import { GlobalFunctions, imagePath, imgTypes } from '@global/GlobalFunctions';
import { FilesService } from '@services/files.service';
import { IIntegratonSensor } from '@services/integration-sensors.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { Account, IAccount } from '@services/accounts.service';

@Component({
	selector: 'app-edit-sensor',
	templateUrl: './edit-sensor.component.html',
	styleUrls: ['./edit-sensor.component.scss']
})
export class EditSensorComponent implements OnInit, OnDestroy, AfterViewInit {

	/** Path to Sensor Images */
	public imagePathSensors(): string {
		return imagePath(this.selectedAccountGUID, imgTypes.sensors).toLowerCase()
	}

	/** Path to Datapoint Images */
	public imagePathDatapoints(): string {
		return imagePath(this.selectedAccountGUID, imgTypes.datapoints).toLowerCase()
	}

	/** Update the Image */
	@Output() imageUpdated = new EventEmitter<any>();

	/** Form Group */
	dialogForm: FormGroup;

	/** Enable the New Picture Button */
	public enablePicture: boolean = true;
	/** Enable the Camera Button */
	public enableCamera: boolean = true;
	/** Enable the Delete Button */
	public enableDelete: boolean = false;

	/** Update Image Instantly */
	public numberRND: number = Math.random();

	/** new Image Selected */
	public newImage: boolean;

	/** File */
	public file: any
	/** Filename */
	public fileName: string
	/** Loading */
	public loading: boolean
	/** shortLink */
	public shortLink: any

	public value: string = '';

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** selectedAccount */
	private selectedAccount: IAccount;

	/** selectedAccountGUID */
	private selectedAccountGUID: string;

	/**
	 * Default Constructor
	 * @param fb Form Builder
	 * @param dialogRef Referense to the Dialog box
	 * @param globalFunctions 
	 * @param filesService 
	 * @param siteSubscriptions 
	 * @param dialogData  object interface
	 */
	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<EditSensorComponent>,
		private globalFunctions: GlobalFunctions,
		private filesService: FilesService,
		private siteSubscriptions: siteSubscriptions,
		@Inject(MAT_DIALOG_DATA) public dialogData: IIntegratonSensor,
	) {
		combineLatest([
			this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([account]) => !!account)
		).subscribe({
			next: ([account]) => {
				this.selectedAccount = account;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;
				this.selectedAccountGUID = this.selectedAccount.GUID;
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false); }
		});
	}

	/**
	 * Page after view init Event Handler
	 */
	ngAfterViewInit(): void { }

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		this.dialogForm = this.fb.group({
			SensorID: [this.dialogData.SensorID || 0, Validators.required],
			PublicSensorID: [this.dialogData.PublicSensorID || "", Validators.required],
			Name: [this.dialogData.Name || "", [Validators.required]],
			Description: [this.dialogData.Description || ""],
			ImageURL: [this.dialogData.ImageURL || ''],
			Modified: [Math.trunc(Math.random() * 100)],
			Enabled: [this.dialogData.Enabled || false],
			EnableOffline: [this.dialogData.EnableOffline || false],
			ImageSrc: this.imagePathSensors() + this.dialogData.SensorID + '?' + this.numberRND
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(); }
				// do nothing if result is false
			});
		} else {
			this.dialogRef.close();
		}
	}

	/**
	 * Save the form data and image if needed
	 */
	async onSaveClick(): Promise<void> {

		if (this.newImage && this.file) {
			try {
				const accountGUID = this.selectedAccountGUID.toLowerCase();
				const fileName = this.dialogData.SensorID.toString();
				const blobLocation = "sensors";
				await this.filesService.saveImage(accountGUID, fileName, this.file, blobLocation); // wait for upload to complete
				this.newImage = false;
				this.file = undefined;
			} // Optional: handle upload failure (maybe prevent closing the dialog)
			catch (error) {
				this.globalFunctions.HandleError(error, "Error: Error Saving Image", true)
				return;
			}
		}

		// Now the upload is done, close the dialog with the form value
		this.dialogRef.close(this.dialogForm.value);
	}

	/**
	 * Delete Image
	 */
	deleteImg(): void {
		// Extract and store the file name
		this.fileName = "transparent.png";
		this.file = new File([""], "transparent.png", { type: "image/png" });
		this.dialogForm.markAsDirty();
		this.newImage = true;
	}



	/**
	 * New File deteced
	 * @param file File from child component
	 */
	onNewImage(file: File | null): void {

		/** Check for a valid file */
		if (file) {
			this.file = file;
			this.newImage = true
		}
		else {
			this.file = undefined;
			this.newImage = false;
		}
		this.dialogForm.markAsDirty();
	}
}
